import { useRef, type KeyboardEvent } from 'react'
import './pipelinePills.css'

export interface PipelineStage {
  key: string
  label: string
  count: number
}

export interface PipelinePillsProps {
  stages: PipelineStage[]
  value?: string
  onChange?: (key: string) => void
  ariaLabel?: string
  testId?: string
  className?: string
}

export const PipelinePills = ({
  stages,
  value,
  onChange,
  ariaLabel,
  testId,
  className,
}: PipelinePillsProps) => {
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([])

  const selectedIndex = stages.findIndex((s) => s.key === value)
  const activeIndex = selectedIndex >= 0 ? selectedIndex : 0

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex = -1
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault()
      nextIndex = (index + 1) % stages.length
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault()
      nextIndex = (index - 1 + stages.length) % stages.length
    } else if (e.key === 'Home') {
      e.preventDefault()
      nextIndex = 0
    } else if (e.key === 'End') {
      e.preventDefault()
      nextIndex = stages.length - 1
    }

    if (nextIndex >= 0 && nextIndex < stages.length) {
      pillRefs.current[nextIndex]?.focus()
      onChange?.(stages[nextIndex].key)
    }
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={['pipelinePills', className].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      {stages.map((stage, i) => {
        const isSelected = stage.key === value
        const isFocusable = i === activeIndex
        return (
          <button
            key={stage.key}
            ref={(el) => {
              pillRefs.current[i] = el
            }}
            type="button"
            className="pipelinePills__pill"
            aria-pressed={isSelected}
            tabIndex={isFocusable ? 0 : -1}
            onClick={() => onChange?.(stage.key)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            data-testid={testId ? `${testId}-stage-${stage.key}` : `pipeline-stage-${stage.key}`}
          >
            <span>{stage.label}</span>
            <span className="pipelinePills__count">{stage.count}</span>
          </button>
        )
      })}
    </div>
  )
}

export default PipelinePills
